/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */

/* eslint-disable sonarjs/cognitive-complexity */
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DialogProps } from '@mui/material';
import { useForm } from 'react-hook-form';

import { FieldNames } from '../../Models/AddEditOneTimeJob.Model';
import { AssignmentCardType, NUMBER_OF_ITEMS_PER_PAGE } from '../../../../Shared/Utilities/utils';
import { BooleanType, JobType } from '../../../../API';
import TasksRequireProofs, { IVendorProofOfCompletion } from './TasksRequireProofs';
import PreShiftChecklist, { IDataProps } from './PreShiftCheckList';
import ShowFacilityDetails, { IAccessFacilityData } from './ShowFacilityDetails';
import AssignTeam from './AssignTeam';

import { Icons } from '../../../../Shared/Constants/Icons';

import { AssignmentCardWrapper } from './OneTimeJobDetails.Style';
import { CloseIconStyle } from '../../../Vendor/Components/CreateBid.Style';

interface ShiftDetails {
  shiftDetailId: string;
  startTime: string;
  endTime: string;
  workOrderId?: string;
  punchInGraceMinutes?: number | null;
  punchOutGraceMinutes?: number | null;
  scheduleStartDate: string;
  scheduleEndDate: string;
}

export interface IAssociateJobConflictData {
  endDate: string;
  endTime: string;
  facilityAddress: string;
  facilityName: string;
  id: string;
  profileImg: string;
  startDate: string;
  startTime: string;
}

export interface ITeamMemberData {
  id: string | null | undefined;
  isCannotMakeJob?: boolean;
  isChecked: boolean;
  isAlreadyChecked?: boolean;
  userId?: string;
  isDisabled?: boolean;
  isDisabledMoreActions?: boolean;
  isJobNotAccepted?: boolean;
  isSupervisor: boolean;
  name: string | null | undefined;
  profileImg: string;
  profileType?: string;
  punchType?: string;
  role: string | null;
  roleId: string | null;
  teamId?: string | undefined;
  associateStartDate?: string | null;
  associateEndDate?: string | null;
  workDayId?: string;
  punchInTime?: string;
  punchOutTime?: string;
}

export interface IWorkOrderTeam {
  cognitoUserId?: string | null;
  isSupervisor?: boolean | null;
  roleId?: string | null;
  teamId: string;
  userId?: string | null;
  vendorId?: string | null;
  workOrderId?: string | null;
}

interface JobAssignmentCard {
  open: boolean;
  onClose: (type: AssignmentCardType) => void;
  assignmentType?: AssignmentCardType;
  jobData: any;
  shiftTimings: ShiftDetails[];
  isLoading: boolean;
  workOrderData: any;
  workOrderId: string;
  subWorkOrderId: string;
  isOnlySubVendor: boolean;
  isOnlyPrimeVendor: boolean;
  hasChild: boolean;
  teamData: ITeamMemberData[];
  setTeamData: (data: ITeamMemberData[]) => void;
  originalTeamData: ITeamMemberData[];
  setOriginalTeamData: (data: ITeamMemberData[]) => void;
  associateTeamData: ITeamMemberData[];
  setAssociateTeamData: (data: ITeamMemberData[]) => void;
  originalAssociateTeamData: ITeamMemberData[];
  setOriginalAssociateTeamData: (data: ITeamMemberData[]) => void;
  checkedSupervisors: any;
  setCheckedSupervisors: (data: any) => void;
  checkedAssociates: any;
  setCheckedAssociates: (data: any) => void;
  isTeamDataLoading: boolean;
  fetchWorkOrderFacilityAccessInfo: (workOrderId: string) => void;
  facilityAccessData: IAccessFacilityData[];
  isFacilityAccessLoading: boolean;
  proofOfServiceAndTaskData: IVendorProofOfCompletion[];
  tasksWithProof: IVendorProofOfCompletion[];
  setProofOfServiceAndTaskData: (data: IVendorProofOfCompletion[]) => void;
  isTaskRequireProofLoading: boolean;
  isPreShiftChecklistLoading: boolean;
  checkListData: IDataProps[];
  setCheckListData: (data: IDataProps[]) => void;
  shiftDetailId: string;
  isAssignSupervisorLoading: boolean;
  isAssignAssociateLoading: boolean;
  renderAssignedSupervisorTeamData: (shiftId: string) => void;
  renderAssignedAssociateTeamData: (shiftId: string) => void;
  getWorkOrderDetails: () => Promise<void>;
  fetchDisableStartJobResult: any;
  fetchScheduleDetails: (workOrderId: string) => Promise<void>;
  totalCount?: number;
  fetchOrganizationUsers?: (
    pageNumber?: number,
    limit?: number,
    isPaginationDataLoader?: boolean,
    searchString?: string,
    isAssociate?: BooleanType,
    alreadyAssignedUsers?: any[]
  ) => void;
  // Selected shift dates (ISO yyyy-mm-dd) for SP self-perform date-picked (Custom) schedules.
  customDates?: string[];
}

const JobAssignmentCard = ({
  open,
  onClose,
  assignmentType,
  jobData,
  shiftTimings,
  workOrderData,
  workOrderId,
  subWorkOrderId,
  isOnlySubVendor,
  isOnlyPrimeVendor,
  hasChild,
  teamData,
  setTeamData,
  originalTeamData,
  setOriginalTeamData,
  associateTeamData,
  setAssociateTeamData,
  originalAssociateTeamData,
  setOriginalAssociateTeamData,
  checkedSupervisors,
  setCheckedSupervisors,
  checkedAssociates,
  setCheckedAssociates,
  isAssignSupervisorLoading,
  isAssignAssociateLoading,
  fetchWorkOrderFacilityAccessInfo,
  facilityAccessData,
  isFacilityAccessLoading,
  tasksWithProof,
  setProofOfServiceAndTaskData,
  isTaskRequireProofLoading,
  isPreShiftChecklistLoading,
  checkListData,
  setCheckListData,
  shiftDetailId,
  renderAssignedSupervisorTeamData,
  renderAssignedAssociateTeamData,
  getWorkOrderDetails,
  fetchDisableStartJobResult,
  fetchScheduleDetails,
  totalCount,
  fetchOrganizationUsers,
  customDates,
}: JobAssignmentCard) => {
  const { t } = useTranslation(['assignJob', 'altTexts', 'oneTimeJob', 'calendar']);
  const formatDateString = (dateString: any) => {
    if (dateString) {
      return dateString.replace(/Z$/, '');
    }
    return dateString;
  };

  const { jobStartDate = '', jobEndDate = '', actualStartDate, actualEndDate } = workOrderData || {};

  const formattedJobStartDate = formatDateString(jobStartDate);
  const formattedJobEndDate = formatDateString(jobEndDate);
  const formattedActualStartDate = formatDateString(actualStartDate);
  const formattedActualEndDate = formatDateString(actualEndDate);
  const facilityId = workOrderData?.facilities?.[0]?.facilityId;
  const { setValue, getValues, watch } = useForm({
    defaultValues: {
      startDate: formattedJobStartDate ?? null,
      endDate: formattedJobEndDate ?? null,
      shiftStartDate: formattedActualStartDate ?? null,
      shiftEndDate: formattedActualEndDate ?? null,
      startTime: (jobData?.jobDetails?.jobFrequencies && jobData?.jobDetails?.jobFrequencies[0]?.fromTime) ?? '',
      endTime: (jobData?.jobDetails?.jobFrequencies && jobData?.jobDetails?.jobFrequencies[0]?.toTime) ?? '',
    },
  });
  const watchFields = watch();
  // const isOneTimeJob = jobData.jobDetails.jobCustodian ? true : false;

  useEffect(() => {
    if (workOrderData) {
      const { jobStartDate, jobEndDate, actualStartDate, actualEndDate, jobType } = workOrderData;

      const formattedJobStartDate = formatDateString(jobStartDate);
      const formattedJobEndDate = formatDateString(jobEndDate);
      const formattedActualStartDate = formatDateString(actualStartDate);
      const formattedActualEndDate = formatDateString(actualEndDate);

      setValue(FieldNames.startDate, formattedJobStartDate);
      setValue(FieldNames.endDate, formattedJobEndDate);
      setValue(FieldNames.shiftStartDate, formattedActualStartDate);
      setValue(FieldNames.shiftEndDate, formattedActualEndDate);
      if (jobType === JobType.Emergency || jobType === JobType.OneTime) {
        setValue(FieldNames.shiftStartDate, formattedJobStartDate);
        setValue(FieldNames.shiftEndDate, formattedJobEndDate);
      }
    }
  }, [workOrderData]);

  const disableAddShiftTimingButton = () => {
    const { startDate, endDate, shiftStartDate, shiftEndDate, startTime, endTime } = getValues();
    if (workOrderData?.jobType === JobType.Recurring || workOrderData?.jobType === JobType.Periodic) {
      return startTime && endTime ? false : true;
    }
    return startDate && endDate && shiftStartDate && shiftEndDate && startTime && endTime ? false : true;
  };

  useEffect(() => {
    disableAddShiftTimingButton();
  }, [watchFields]);

  const handleClose: DialogProps['onClose'] = (event, reason) => {
    if (reason && reason === 'backdropClick') return;
    onClose(assignmentType as AssignmentCardType);
  };

  return (
    <AssignmentCardWrapper open={open} onClose={handleClose}>
      <CloseIconStyle onClick={() => onClose(assignmentType as AssignmentCardType)}>
        <img src={Icons.InfoWindowCloseIcon} alt={t('altTexts:closeIcon')} />
      </CloseIconStyle>
      {/* {assignmentType === AssignmentCardType.ShiftTimings && renderShiftTimingsContent()} */}
      {assignmentType === AssignmentCardType.AssignSupervisor && (
        <AssignTeam
          teamData={teamData}
          shiftDetailId={shiftDetailId}
          shiftTimings={shiftTimings}
          setTeamData={setTeamData}
          originalTeamData={originalTeamData}
          setOriginalTeamData={setOriginalTeamData}
          associateTeamData={associateTeamData}
          setAssociateTeamData={setAssociateTeamData}
          originalAssociateTeamData={originalAssociateTeamData}
          setOriginalAssociateTeamData={setOriginalAssociateTeamData}
          checkedSupervisors={checkedSupervisors}
          setCheckedSupervisors={setCheckedSupervisors}
          checkedAssociates={checkedAssociates}
          setCheckedAssociates={setCheckedAssociates}
          isTeamDataLoading={isAssignSupervisorLoading}
          facilityId={facilityId}
          workOrderId={workOrderId}
          onClose={onClose}
          isViewOnly={hasChild}
          assignmentType={assignmentType}
          workOrderData={workOrderData}
          renderAssignedSupervisorTeamData={renderAssignedSupervisorTeamData}
          renderAssignedAssociateTeamData={renderAssignedAssociateTeamData}
          getWorkOrderDetails={getWorkOrderDetails}
          fetchDisableStartJobResult={fetchDisableStartJobResult}
          fetchScheduleDetails={fetchScheduleDetails}
          jobData={jobData}
          totalCount={totalCount}
          fetchOrganizationUsers={(pageNumber: number, searchString: string) =>
            fetchOrganizationUsers?.(
              pageNumber,
              NUMBER_OF_ITEMS_PER_PAGE,
              true,
              searchString,
              BooleanType.False,
              checkedSupervisors
            )
          }
        />
      )}
      {assignmentType === AssignmentCardType.AssignAssociate && (
        <AssignTeam
          teamData={teamData}
          shiftDetailId={shiftDetailId}
          shiftTimings={shiftTimings}
          setTeamData={setTeamData}
          originalTeamData={originalTeamData}
          setOriginalTeamData={setOriginalTeamData}
          associateTeamData={associateTeamData}
          setAssociateTeamData={setAssociateTeamData}
          originalAssociateTeamData={originalAssociateTeamData}
          setOriginalAssociateTeamData={setOriginalAssociateTeamData}
          checkedSupervisors={checkedSupervisors}
          setCheckedSupervisors={setCheckedSupervisors}
          checkedAssociates={checkedAssociates}
          setCheckedAssociates={setCheckedAssociates}
          isTeamDataLoading={isAssignAssociateLoading}
          facilityId={facilityId}
          workOrderId={workOrderId}
          onClose={onClose}
          isViewOnly={hasChild}
          assignmentType={assignmentType}
          workOrderData={workOrderData}
          renderAssignedSupervisorTeamData={renderAssignedSupervisorTeamData}
          renderAssignedAssociateTeamData={renderAssignedAssociateTeamData}
          getWorkOrderDetails={getWorkOrderDetails}
          fetchDisableStartJobResult={fetchDisableStartJobResult}
          fetchScheduleDetails={fetchScheduleDetails}
          jobData={jobData}
          totalCount={totalCount}
          customDates={customDates}
          fetchOrganizationUsers={(pageNumber: number, searchString: string) =>
            fetchOrganizationUsers?.(
              pageNumber,
              NUMBER_OF_ITEMS_PER_PAGE,
              true,
              searchString,
              BooleanType.True,
              checkedAssociates
            )
          }
        />
      )}
      {assignmentType === AssignmentCardType.FacilityAccess && (
        <ShowFacilityDetails
          workOrderId={workOrderId}
          onClose={onClose}
          fetchWorkOrderFacilityAccessInfo={fetchWorkOrderFacilityAccessInfo}
          facilityAccessData={facilityAccessData}
          isLoading={isFacilityAccessLoading}
          assignmentType={assignmentType}
        />
      )}
      {assignmentType === AssignmentCardType.ProofOfCompletionTasks && (
        <TasksRequireProofs
          workOrderId={workOrderId}
          subWorkOrderId={subWorkOrderId}
          isOnlySubVendor={isOnlySubVendor}
          workOrderData={workOrderData}
          onClose={onClose}
          proofOfServiceAndTaskData={tasksWithProof}
          setProofOfServiceAndTaskData={setProofOfServiceAndTaskData}
          isDataLoading={isTaskRequireProofLoading}
          assignmentType={assignmentType}
          fetchScheduleDetails={fetchScheduleDetails}
        />
      )}
      {assignmentType === AssignmentCardType.PreShiftChecklist && (
        <PreShiftChecklist
          isOnlySubVendor={isOnlySubVendor}
          isOnlyPrimeVendor={isOnlyPrimeVendor}
          workOrderId={workOrderId}
          subWorkOrderId={subWorkOrderId}
          onClose={onClose}
          isDataLoading={isPreShiftChecklistLoading}
          checkListData={checkListData}
          setCheckListData={setCheckListData}
          assignmentType={assignmentType}
          shiftDetailId={shiftDetailId}
        />
      )}
    </AssignmentCardWrapper>
  );
};

export default JobAssignmentCard;
